import type { ReactNode } from 'react';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { TextInput } from '@/components/base/TextInput';
import { t } from '@/messages/zh-TW';
import type { EffectKey } from '@/mocks/endpoint-map';
import { Pagination } from './Pagination';

/**
 * 第二層版型：**全站共用的 DataTable 殼層**。
 *
 * ── 為什麼有這個元件 ───────────────────────────────────────────────────────
 * `AUDIT_GATE3_HANDOFF.md` §1.1：DataTables 的共用 chrome 八項在全部 13 個線稿 part 檔中
 * 完全缺席（主控實測「查無符合 0 次、Bfrtip 0 次、每頁顯示 0 次」）。原因是這些 UI 由
 * JS 在 runtime 生成，靜態抽取 JSP markup 的線稿方法論不可能抓到。稽核結論是
 * 「應畫一張全站共用的 DataTable 殼層規格圖，而非逐頁補 385 次」—— 本元件就是那張圖的實作。
 *
 * ── 八項 chrome 的原始依據（`WebContent/js-my/common.js`，`util.DataTableInitByCustomConfig`）──
 *   `:795`      函式進入點；`DataTableInit`（`:780-782`）以 `{ fixedHeader: true }` 呼叫它
 *   `:828`      `dom: 'Bfrtip'` → B(buttons) f(filter) r(processing) t(table) i(info) p(pagination)
 *   ① 匯出鈕    `:829-857` `buttons: [{extend:'pageLength'}, {extend:'excel', exportOptions:{
 *                columns: ['.export','.important'], …}}]` —— 只匯出帶 `.export`／`.important`
 *                class 的欄位，`format.body` 會把儲存格內的 input/radio/checkbox 轉成文字
 *   ② 每頁筆數  `:862` `lengthMenu: [[10,20,30,60,100],[10,20,30,60,100]]`
 *                `:800` `"lengthMenu": "顯示 _MENU_ 項結果"`
 *                `:813-818`（值在 `:815`）`buttons.pageLength._` = `"每頁顯示 %d 項結果"`
 *                ※ `dom` 字串裡**沒有 `l`**，所以原系統的每頁筆數其實是 `pageLength` 這顆
 *                  **按鈕式下拉**（B 區），不是 DataTables 原生的 length select。
 *                  雛形用一般 `<Select>` 模擬，外觀差異列為【雛形值待實機校準】。
 *   ③ 關鍵字查詢 `:806` `"search": "關鍵字查詢:"`（dom 的 `f`）
 *   ④ 分頁列    `:827` `sPaginationType: "full_numbers"`；`:807-812` 第一頁／上一頁／下一頁／最後一頁
 *   ⑤ 筆數資訊  `:802` `"info": "顯示第 _START_ 至 _END_ 項結果，共 _TOTAL_ 項"`
 *                `:803` `"infoEmpty": "顯示第 0 至 0 項結果，共 0 項"`
 *                `:804` `"infoFiltered": "(從 _MAX_ 項結果中過濾)"`
 *   ⑥ 處理中    `:860` `processing: true`；`:798` `"processing": "處理中..."`
 *                （另有 `:799` `"loadingRecords": "載入中..."`，用在 ajax 首次載入）
 *   ⑦ 空表文案  `:801` `"zeroRecords": "沒有符合的結果"`
 *                `:823` `"sZeroRecords": '查無符合的資料'`
 *                ※ **兩個 key 文案不同**。`sZeroRecords` 是 DataTables 1.9 的舊命名，
 *                  1.10+ 以 `zeroRecords` 為準，因此**實際畫面上出現的很可能是
 *                  「沒有符合的結果」**，而稽核報告與 `zh-TW.ts` 的 `table.empty` 用的是
 *                  「查無符合的資料」。本元件預設用 `t('table.empty')`（與稽核一致），
 *                  實際生效的是 sZeroRecords（追溯過程見 zh-TW.ts 的 table.empty 註解），zeroRecords 不會顯示。
 *                  **這一項必須在原系統跑得起來時實機確認**，目前只是原始碼推論。
 *   ⑧ responsive `:865` `responsive: true`（外加 `:859` `scrollX: true`、`:861` `bStateSave: true`）
 *
 * ── 明確不做的事 ─────────────────────────────────────────────────────────
 * §3.2 禁止引入 DataTables 與 jQuery，所以本元件只**模擬可見外觀與互動**：
 * ponytail: 分頁／過濾一律由呼叫端用 React state 自己算（本元件是「受控殼層」，
 * 不持有任何資料），排序與 responsive 的欄位折疊（⊕ 展開子列）不實作，
 * 只用 `overflow-x-auto` 對應 `scrollX: true`。升級路徑：需要欄位折疊時，
 * 在本元件內加一層 container query，不需要改呼叫端。
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export interface DataTableShellProps {
  uiId: string;
  /** 目前頁碼，1 起算。 */
  page: number;
  pageSize: number;
  /** 套用關鍵字之後的總筆數（DataTables 的 `_TOTAL_`）。 */
  total: number;
  /** 未過濾前的總筆數（`_MAX_`）。大於 `total` 時才顯示 infoFiltered。 */
  totalUnfiltered?: number;
  keyword?: string;
  /** true 時蓋上「處理中...」遮罩（common.js:798,860）。 */
  loading?: boolean;
  /** common.js:862 的 lengthMenu。 */
  pageSizeOptions?: readonly number[];
  onPageChange(page: number): void;
  onPageSizeChange(pageSize: number): void;
  onKeywordChange(keyword: string): void;
  /** 匯出 Excel。沒有傳時按鈕仍然畫出來但 disabled（原系統一律有這顆鈕）。 */
  onExport?(): void;
  /**
   * 匯出鈕的 `data-effect`（D-1 第 2a 點的變體後綴，形如 `list:<resource>#export`）。
   * 呼叫端只有在 `onExport` 真的會打 `useDataSource().list()` 時才可以傳（criteria C5-j）；
   * `onExport` 未傳（按鈕 disabled）時本殼層也不會輸出屬性。
   */
  exportEffect?: EffectKey;
  /** 覆寫空表文案；預設 `t('table.empty')`。 */
  emptyText?: string;
  /**
   * 表格本體，通常是第一層的 `<Table>`。
   * 注意：空表文案由本殼層負責（`data-ui-id={uiId}-empty`），
   * 呼叫端不要再設 `<Table emptyText>`，否則同一個畫面會出現兩次。
   */
  children: ReactNode;
}

/** common.js:862 lengthMenu 的預設值。 */
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 30, 60, 100] as const;

export function DataTableShell({
  uiId,
  page,
  pageSize,
  total,
  totalUnfiltered,
  keyword = '',
  loading = false,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
  onKeywordChange,
  onExport,
  exportEffect,
  emptyText,
  children,
}: DataTableShellProps) {
  const pageCount = pageSize > 0 ? Math.ceil(total / pageSize) : 0;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const isEmpty = !loading && total === 0;
  const filtered = totalUnfiltered !== undefined && totalUnfiltered > total;

  return (
    <div data-ui-id={uiId} className="relative flex flex-col gap-2">
      {/* B + f 區（common.js:828 dom 的 'B' 與 'f'） */}
      <div
        data-ui-id={`${uiId}-toolbar`}
        className="flex flex-wrap items-center gap-2 border-border border-b pb-2"
      >
        {/* ② 每頁筆數下拉。
            用 <label> 包住控制項做隱含關聯 —— 第一層的 Select/TextInput 沒有 `id` prop，
            htmlFor 會指到不存在的節點。TODO(base): 第一層加上 id prop 後可改回顯式關聯。 */}
        {/* biome-ignore lint/a11y/noLabelWithoutControl: 控制項是第一層 <Select>，
            實際 render 出 <select>；biome 看不穿自訂元件。 */}
        <label data-ui-id={`${uiId}-page-size`} className="flex items-center gap-2 text-sm">
          <span data-ui-id={`${uiId}-page-size-label`} className="whitespace-nowrap">
            {t('table.pageSize')}
          </span>
          <Select
            uiId={`${uiId}-page-size-select`}
            value={String(pageSize)}
            disabled={loading}
            onChange={(next) => onPageSizeChange(Number(next))}
            options={pageSizeOptions.map((size) => ({
              value: String(size),
              label: t('table.pageSizeOption', { size }),
            }))}
          />
        </label>

        {/* ① Excel 匯出鈕 */}
        <Button
          uiId={`${uiId}-export`}
          variant="default"
          size="sm"
          type="button"
          disabled={loading || onExport === undefined}
          {...(onExport === undefined
            ? {}
            : {
                onClick: onExport,
                ...(exportEffect === undefined ? {} : { dataEffect: exportEffect }),
              })}
        >
          {t('action.export')}
        </Button>

        {/* ③ 關鍵字查詢框。
            文案「關鍵字查詢:」出自 common.js:806，含尾端冒號，照實保留。
            （原本臆造的 table.search='搜尋' 已在訊息層統一時刪除。） */}
        {/* biome-ignore lint/a11y/noLabelWithoutControl: 控制項是第一層 <TextInput>，
            實際 render 出 <input>；biome 看不穿自訂元件。 */}
        <label data-ui-id={`${uiId}-search`} className="ml-auto flex items-center gap-2 text-sm">
          <span data-ui-id={`${uiId}-search-label`} className="whitespace-nowrap">
            {t('table.searchLabel')}
          </span>
          <TextInput
            uiId={`${uiId}-search-input`}
            value={keyword}
            disabled={loading}
            onChange={onKeywordChange}
          />
        </label>
      </div>

      {/* ⑧ responsive：對應 common.js:859 scrollX / :865 responsive。
          雛形只做水平捲動，不做欄位折疊子列。 */}
      <div data-ui-id={`${uiId}-scroll`} className="overflow-x-auto">
        {children}
        {/* ⑦ 空表文案 */}
        {isEmpty ? (
          <p data-ui-id={`${uiId}-empty`} className="py-6 text-center text-ink-muted text-sm">
            {emptyText ?? t('table.empty')}
          </p>
        ) : null}
      </div>

      {/* i + p 區 */}
      <div
        data-ui-id={`${uiId}-footer`}
        className="flex flex-wrap items-center justify-between gap-2"
      >
        {/* ⑤ 筆數資訊列。
            zh-TW.ts 的 table.info 是「顯示第 {start} 到 {end} 筆，共 {total} 筆」，
            原系統是「顯示第 _START_ 至 _END_ 項結果，共 _TOTAL_ 項」（common.js:802），
            用字不同但語意相同。TODO(messages): 是否改為原文，待裁定。 */}
        <p data-ui-id={`${uiId}-info`} className="text-sm">
          {total === 0 ? t('table.infoEmpty') : t('table.info', { start, end, total })}
          {filtered ? (
            <span data-ui-id={`${uiId}-info-filtered`}>
              {' '}
              {t('table.infoFiltered', { max: totalUnfiltered ?? total })}
            </span>
          ) : null}
        </p>

        {/* ④ 分頁列 */}
        <Pagination
          uiId={`${uiId}-pagination`}
          page={page}
          pageCount={pageCount}
          onChange={onPageChange}
          disabled={loading}
        />
      </div>

      {/* ⑥ 處理中… 載入指示。
          原系統是 DataTables 的 processing 區塊（common.js:860 `processing: true`），
          蓋在表格上方；aria-live 讓讀屏能播報，這是雛形加的無障礙基本盤。 */}
      {loading ? (
        <div
          data-ui-id={`${uiId}-processing`}
          role="status"
          aria-live="polite"
          className="absolute inset-0 flex items-center justify-center bg-surface/70 text-sm"
        >
          {t('table.processing')}
        </div>
      ) : null}
    </div>
  );
}
